import {
	dxtToLocalServerTime,
	getMomentTz,
	TimeService,
} from "@dexteel/mesf-core";
import { Done, DoneAll } from "@mui/icons-material";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Event, EventSegment } from "../models/Event";
import { billetStatus } from "../shared/billetStatus";
import { eventTypes, productionTypes } from "../shared/eventTypes";
import {
	addDays,
	countSectionsByStatus,
	getDayKey,
	isDayInCurrentMonth,
	isToday,
	organizeEventsInRows,
	startOfMonth,
	startOfWeek,
} from "../utils/calendarUtils";
import { getEventStyle } from "../utils/eventStyles";
import { formatDurationMs } from "../utils/formatDuration";

const moment = getMomentTz();

const getNarrowBorderLeft = (borderLeft?: string | number) => {
	if (typeof borderLeft !== "string") return borderLeft;

	const width = Number.parseInt(borderLeft, 10);
	if (!width || width <= 10) return borderLeft;

	return borderLeft.replace(/^\d+px/, `${10}px`);
};

interface EnhancedMonthCalendarProps {
	events: Event[];
	initialDate?: Date;
	weekStartsOn?: 0 | 1;
	eventsLimitPerDay?: number;
	eventLimitText?: string;
}

export const EnhancedMonthCalendar = ({
	events = [],
	initialDate = new Date(),
	weekStartsOn = 0,
	eventsLimitPerDay = 3,
	eventLimitText = "+{count} more",
}: EnhancedMonthCalendarProps) => {
	const [currentDate, setCurrentDate] = useState(initialDate);
	const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
	const [hoveredBilletStatusCode, setHoveredBilletStatusCode] = useState<
		string | null
	>(null);
	const [swapProductionColors, setSwapProductionColors] = useState(false);

	const calendarDays = useMemo(() => {
		const monthStart = startOfMonth(currentDate);
		const calendarStart = startOfWeek(monthStart, weekStartsOn);

		const days: Date[] = [];
		let day = calendarStart;
		for (let i = 0; i < 42; i++) {
			days.push(new Date(day));
			day = addDays(day, 1);
		}

		return days;
	}, [currentDate, weekStartsOn]);

	const weeks = useMemo(() => {
		const weeksArray: Date[][] = [];
		for (let i = 0; i < calendarDays.length; i += 7) {
			weeksArray.push(calendarDays.slice(i, i + 7));
		}

		return weeksArray;
	}, [calendarDays]);

	// Process events into segments
	const eventSegments = useMemo(() => {
		const segments: EventSegment[] = [];
		const serverTz = TimeService.getInstance().getServerTimeZone();

		events.forEach((event) => {
			const eventStart = moment.tz(event.Start, serverTz);
			const eventEnd = moment.tz(event.End!, serverTz);

			weeks.forEach((week, weekIndex) => {
				let firstDayIndex = -1;
				let lastDayIndex = -1;

				week.forEach((day, dayIndex) => {
					const year = day.getFullYear();
					const month = String(day.getMonth() + 1).padStart(2, "0");
					const dayOfMonth = String(day.getDate()).padStart(2, "0");
					const dayStr = `${year}-${month}-${dayOfMonth}`;

					const dayInServerTz = moment.tz(dayStr, serverTz);
					const dayStart = dayInServerTz.clone().startOf("day");
					const dayEnd = dayInServerTz.clone().endOf("day");

					const eventIsInThisDay =
						eventStart.isBefore(dayEnd) && eventEnd.isAfter(dayStart);

					if (eventIsInThisDay) {
						if (firstDayIndex === -1) firstDayIndex = dayIndex;
						lastDayIndex = dayIndex;
					}
				});

				if (firstDayIndex !== -1) {
					const firstDayOfWeek = week[firstDayIndex];
					const lastDayOfWeek = week[lastDayIndex];

					const firstYear = firstDayOfWeek.getFullYear();
					const firstMonth = String(firstDayOfWeek.getMonth() + 1).padStart(
						2,
						"0",
					);
					const firstDay = String(firstDayOfWeek.getDate()).padStart(2, "0");
					const firstDayStr = `${firstYear}-${firstMonth}-${firstDay}`;

					const lastYear = lastDayOfWeek.getFullYear();
					const lastMonth = String(lastDayOfWeek.getMonth() + 1).padStart(
						2,
						"0",
					);
					const lastDay = String(lastDayOfWeek.getDate()).padStart(2, "0");
					const lastDayStr = `${lastYear}-${lastMonth}-${lastDay}`;

					const eventStartsThisDay =
						eventStart.format("YYYY-MM-DD") === firstDayStr;
					const eventEndsThisDay = eventEnd.format("YYYY-MM-DD") === lastDayStr;

					segments.push({
						event,
						dayIndex: firstDayIndex,
						weekIndex,
						isStart: eventStartsThisDay,
						isEnd: eventEndsThisDay,
						segStart: eventStart.toDate(),
						segEnd: eventEnd.toDate(),
						originalStart: eventStart.toDate(),
						originalEnd: eventEnd.toDate(),
						spanDays: lastDayIndex - firstDayIndex + 1,
						firstDayInWeek: firstDayIndex,
						lastDayInWeek: lastDayIndex,
					});
				}
			});
		});

		return segments;
	}, [events, weeks]);

	const segmentsByWeekAndDayWithRows = useMemo(() => {
		const grouped: { segments: EventSegment[]; rows: EventSegment[][] }[][] =
			weeks.map(() =>
				Array(7)
					.fill(null)
					.map(() => ({ segments: [], rows: [] })),
			);

		eventSegments.forEach((segment) => {
			const dayData = grouped[segment.weekIndex][segment.dayIndex];
			dayData.segments.push(segment);
		});

		grouped.forEach((week) => {
			// Collect all segments in this week
			const allWeekSegments: EventSegment[] = [];
			week.forEach((dayData) => {
				dayData.segments.sort(
					(a, b) => a.originalStart.getTime() - b.originalStart.getTime(),
				);
				allWeekSegments.push(...dayData.segments);
			});

			// Remove duplicates (same event appearing in multiple days)
			const uniqueSegments = allWeekSegments.filter(
				(segment, index, self) =>
					index ===
					self.findIndex((s) => s.event.EventId === segment.event.EventId),
			);

			// Organize all week segments into rows
			const weekRows = organizeEventsInRows(uniqueSegments);

			// Create a map of eventId to rowIndex
			const eventToRowMap = new Map<number, number>();
			weekRows.forEach((row, rowIndex) => {
				row.forEach((segment) => {
					eventToRowMap.set(segment.event.EventId, rowIndex);
				});
			});

			// Assign the correct row to each segment based on the week-level organization
			week.forEach((dayData) => {
				const rowsForDay: EventSegment[][] = [];

				dayData.segments.forEach((segment) => {
					const rowIndex = eventToRowMap.get(segment.event.EventId) ?? 0;

					// Ensure the row exists
					while (rowsForDay.length <= rowIndex) {
						rowsForDay.push([]);
					}

					rowsForDay[rowIndex].push(segment);
				});

				dayData.rows = rowsForDay;
			});
		});

		return grouped;
	}, [eventSegments, weeks]);

	const dayNames = useMemo(() => {
		const names: string[] = [];
		const baseDate = startOfWeek(new Date(), weekStartsOn);
		for (let i = 0; i < 7; i++) {
			const day = addDays(baseDate, i);
			names.push(
				day.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase(),
			);
		}
		return names;
	}, [weekStartsOn]);

	const toggleDayExpansion = (date: Date) => {
		const key = getDayKey(date);
		setExpandedDays((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(key)) {
				newSet.delete(key);
			} else {
				newSet.add(key);
			}
			return newSet;
		});
	};

	useEffect(() => {
		setCurrentDate(initialDate);
	}, [initialDate]);

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "auto",
				userSelect: "none",
			}}
		>
			{/* Day names header */}
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(7, 1fr)",
					borderBottom: "1px solid #e0e0e0",
					backgroundColor: (theme) => theme.palette.primary.main,
					borderRadius: 1,
				}}
			>
				{dayNames.map((name, index) => (
					<Box
						key={index}
						sx={{
							px: 1,
							py: 0.5,
							textAlign: "center",
							fontSize: "0.675rem",
							fontWeight: 500,
							color: (theme) => theme.palette.primary.contrastText,
							borderRight: index < 6 ? "1px solid #f0f0f0" : "none",
						}}
					>
						{name}
					</Box>
				))}
			</Box>

			{/* Calendar grid */}
			<Box sx={{ flex: 1, overflow: "hidden" }}>
				{weeks.map((week, weekIndex) => (
					<Box
						key={weekIndex}
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(7, 1fr)",
							minHeight: "120px",
							borderBottom: "1px solid #e0e0e0",
						}}
					>
						{week.map((day, dayIndex) => {
							const isCurrentMonth = isDayInCurrentMonth(day, currentDate);
							const isDayToday = isToday(day);
							const dayData = segmentsByWeekAndDayWithRows[weekIndex][dayIndex];
							const eventRows = dayData.rows;
							const dayKey = getDayKey(day);
							const isExpanded = expandedDays.has(dayKey);

							const visibleRows = isExpanded
								? eventRows
								: eventRows.slice(0, eventsLimitPerDay);
							const hiddenCount = eventRows.length - visibleRows.length;

							return (
								<Box
									key={dayIndex}
									data-date={day.toISOString().split("T")[0]}
									sx={{
										borderRight: dayIndex < 6 ? "1px solid #e0e0e0" : "none",
										py: 1,
										backgroundColor: isCurrentMonth ? "white" : "#f7f7f7ff",
										position: "relative",
										overflow: "visible",
									}}
								>
									{/* Day number */}
									<Box
										sx={{
											display: "flex",
											justifyContent: "flex-end",
											marginBottom: 0.5,
										}}
									>
										<Typography
											sx={{
												fontSize: "0.875rem",
												fontWeight: 500,
												width: 28,
												height: 28,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												borderRadius: "50%",
												backgroundColor: (theme) =>
													isDayToday
														? theme?.palette?.primary?.main || "#1976d2"
														: "transparent",
												color: isDayToday
													? "#fff"
													: !isCurrentMonth
														? "grey"
														: "inherit",
											}}
										>
											{day.getDate()}
										</Typography>
									</Box>

									{/* Events organized by rows */}
									<Box
										sx={{
											position: "relative",
											minHeight: `${
												visibleRows.length * 26 + (hiddenCount > 0 ? 20 : 0)
											}px`,
											overflow: "visible",
										}}
									>
										{visibleRows.map((row, rowIndex) => {
											return row.map((segment) => {
												const isFirstDay =
													(segment.firstDayInWeek ?? segment.dayIndex) ===
													dayIndex;

												if (!isFirstDay) return null;

												const span = segment.spanDays || 1;
												const segIndex = `${segment.event.EventId}-${rowIndex}-${dayIndex}`;

												const eventStyles = getEventStyle(
													segment,
													hoveredBilletStatusCode,
													swapProductionColors,
												);
												const eventType = eventTypes.find(
													(t) => t.code === segment.event?.EventTypeCode,
												);

												const isCompleted = segment.event.StatusCode === "C";
												const isConfirmed = segment.event.StatusCode === "K";

												// Calculate the actual width percentage for the event
												const eventWidthPercent =
													span > 1
														? (() => {
																const serverTz =
																	TimeService.getInstance().getServerTimeZone();
																const momentStart = moment.tz(
																	segment.segStart,
																	serverTz,
																);
																const momentEnd = moment.tz(
																	segment.segEnd,
																	serverTz,
																);

																let totalWidth = 0;

																if (segment.isStart) {
																	const startPercent =
																		((momentStart.hour() * 60 +
																			momentStart.minute()) /
																			(24 * 60)) *
																		100;
																	totalWidth += 100 - startPercent;
																} else {
																	totalWidth += 100;
																}

																totalWidth += (span - 2) * 100;

																if (segment.isEnd) {
																	const endPercent =
																		((momentEnd.hour() * 60 +
																			momentEnd.minute()) /
																			(24 * 60)) *
																		100;
																	totalWidth += endPercent;
																} else {
																	totalWidth += 100;
																}

																return totalWidth;
															})()
														: (() => {
																const serverTz =
																	TimeService.getInstance().getServerTimeZone();
																const momentStart = moment.tz(
																	segment.segStart,
																	serverTz,
																);
																const momentEnd = moment.tz(
																	segment.segEnd,
																	serverTz,
																);
																const startPercent =
																	((momentStart.hour() * 60 +
																		momentStart.minute()) /
																		(24 * 60)) *
																	100;
																const endPercent =
																	((momentEnd.hour() * 60 +
																		momentEnd.minute()) /
																		(24 * 60)) *
																	100;
																return endPercent - startPercent;
															})();

												const isNarrowEvent = eventWidthPercent < 50;

												return (
													<Tooltip
														key={segIndex}
														title={
															<Box>
																<Typography
																	variant="body2"
																	sx={{ fontWeight: 600 }}
																>
																	{segment.event.EventTypeCode === "P"
																		? `Section: ${segment.event.SectionName}`
																		: eventType?.name}
																</Typography>
																{segment.event.EventTypeCode === "P" ? (
																	<Box display="flex" gap={0.5}>
																		<Typography variant="caption">
																			{segment.event.StatusName}
																		</Typography>
																		<Typography variant="caption">
																			{segment.event?.ActualRolledTons
																				? `• ${segment.event?.ActualRolledTons.toFixed(
																						0,
																					)} tons`
																				: ""}
																		</Typography>
																	</Box>
																) : (
																	<Box display="flex" gap={0.5}>
																		<Typography variant="caption">
																			{segment.event.EventComments}
																		</Typography>
																	</Box>
																)}
																<Typography variant="caption">
																	{dxtToLocalServerTime(
																		segment.originalStart,
																		"MM/dd HH:mm",
																	)}{" "}
																	-{" "}
																	{dxtToLocalServerTime(
																		segment.originalEnd,
																		"MM/dd HH:mm",
																	)}
																</Typography>
																<Typography variant="caption" display="block">
																	Duration:{" "}
																	{formatDurationMs(
																		segment.originalEnd.getTime() -
																			segment.originalStart.getTime(),
																	)}
																</Typography>
															</Box>
														}
													>
														<Box
															data-event-id={segment.event.EventId}
															sx={{
																position: "absolute",
																top: `${rowIndex * 26}px`,
																left: 0,
																zIndex: 1,
																cursor: "default",
																borderRadius: "4px",
																padding: "2px 6px",
																fontSize: "0.75rem",
																whiteSpace: "nowrap",
																textOverflow: "ellipsis",
																overflow: "visible",
																backgroundColor: eventStyles.backgroundColor,
																borderLeft: segment.isStart
																	? isNarrowEvent
																		? getNarrowBorderLeft(
																				eventStyles.borderLeft,
																			)
																		: eventStyles.borderLeft
																	: "none",
																color: eventStyles.color,
																height: "25px",
																width:
																	span > 1
																		? `calc(${eventWidthPercent}% + ${
																				span - 1
																			}px)`
																		: eventStyles.width,
																marginLeft: eventStyles.marginLeft,
																"&:hover": {
																	opacity: 0.9,
																	boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
																	zIndex: 1,
																	overflow: "visible",
																},
																transition: "all 0.2s ease",
																clipPath: "inset(0 0 0 -10px)",
															}}
														>
															{segment.isStart &&
																isCompleted &&
																!isNarrowEvent && (
																	<Done
																		sx={{
																			position: "absolute",
																			left: "-22px",
																			top: "50%",
																			transform: "translateY(-50%)",
																			fontSize: "16px",
																			color: "#065F46",
																			zIndex: 999,
																		}}
																	/>
																)}

															{segment.isStart &&
																isConfirmed &&
																!isNarrowEvent && (
																	<DoneAll
																		sx={{
																			position: "absolute",
																			left: "-22px",
																			top: "50%",
																			transform: "translateY(-50%)",
																			fontSize: "16px",
																			color: "#065F46",
																			zIndex: 999,
																		}}
																	/>
																)}

															<>
																{segment.event.EventTypeCode === "P"
																	? segment.event?.SectionName
																		? `${segment.event?.SectionName}`
																		: ""
																	: `${eventType?.name}`}
																{eventType?.code === "P"
																	? ` • ${
																			productionTypes.find(
																				(t) =>
																					t.code === segment.event?.StatusCode,
																			)?.name ?? ""
																		}${`${
																			segment.event?.ActualRolledTons
																				? ` • ${segment.event?.ActualRolledTons.toFixed(
																						0,
																					)} tons`
																				: ""
																		}`}`
																	: `${segment.event.EventComments ? ` • ${segment.event.EventComments}` : ""}`}
															</>
														</Box>
													</Tooltip>
												);
											});
										})}

										{/* Show more button */}
										{hiddenCount > 0 && (
											<Button
												size="small"
												onClick={() => {
													toggleDayExpansion(day);
												}}
												sx={{
													position: "absolute",
													top: `${visibleRows.length * 26}px`,
													left: 0,
													fontSize: "0.7rem",
													textTransform: "none",
													padding: "2px 6px",
													minWidth: "auto",
													color: (theme) =>
														theme?.palette?.primary?.main || "#1976d2",
													zIndex: 1,
												}}
											>
												{eventLimitText.replace(
													"{count}",
													hiddenCount.toString(),
												)}
											</Button>
										)}
									</Box>
								</Box>
							);
						})}
					</Box>
				))}
			</Box>

			{/* Billet Status Legend */}
			<Box
				style={{
					display: "flex",
					gap: 10,
					padding: 2,
					flexWrap: "wrap",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Box style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
					{billetStatus.map((type) => (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: (theme) => theme.spacing(1),
								cursor: "pointer",
								px: 1,
								py: 0.5,
								borderRadius: 1,
								backgroundColor:
									hoveredBilletStatusCode === type.code
										? "#e0e0e0"
										: "transparent",
								transition: "background-color 0.2s",
							}}
							key={type.code}
							onMouseEnter={() => setHoveredBilletStatusCode(type.code)}
							onMouseLeave={() => setHoveredBilletStatusCode(null)}
						>
							<Box
								sx={{
									width: 15,
									height: 15,
									borderRadius: 4,
									backgroundColor: type.backgroundColor,
								}}
							/>
							<Typography variant="body2" color="textSecondary">
								Billets {type.name}
								{` (${countSectionsByStatus(eventSegments, type.code)})`}
							</Typography>
						</Box>
					))}
				</Box>
				<FormControlLabel
					control={
						<Checkbox
							checked={swapProductionColors}
							onChange={(e) => setSwapProductionColors(e.target.checked)}
						/>
					}
					label="Swap Production Colors"
				/>
			</Box>
		</Box>
	);
};
